import React, { useState } from 'react';
import {
    EditorialNav,
    EditorialFooter,
    Eyebrow,
    mono,
    serif,
    hairline,
    ivory,
    bodyText,
    muted,
    subtle,
} from './landing/EditorialShell';
import '../assets/scss/landing-v2.scss';
import '../assets/scss/editorial-prose.scss';

interface FormState {
    name: string;
    email: string;
    subject: string;
    message: string;
}

const inputBase: React.CSSProperties = {
    width: '100%',
    background: 'rgba(232, 230, 224, 0.03)',
    border: '1px solid rgba(232, 230, 224, 0.1)',
    borderRadius: 4,
    padding: '12px 14px',
    color: ivory,
    fontFamily: 'inherit',
    fontSize: 14,
    outline: 'none',
};

const labelBase: React.CSSProperties = {
    display: 'block',
    fontSize: 10,
    color: muted,
    letterSpacing: '0.18em',
    fontFamily: mono,
    fontWeight: 500,
    marginBottom: 8,
    textTransform: 'uppercase',
};

const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState<FormState>({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    return (
        <div
            className="lp2"
            style={{
                background: '#0a0a0a',
                color: ivory,
                fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, sans-serif",
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <EditorialNav />
            <main style={{ flex: 1 }}>
                <section
                    style={{
                        padding: 'clamp(56px, 9vw, 120px) clamp(20px, 4vw, 40px) clamp(64px, 10vw, 120px)',
                    }}
                >
                    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
                        <div style={{ marginBottom: 56, maxWidth: 720 }}>
                            <Eyebrow>CONTACT</Eyebrow>
                            <h1
                                style={{
                                    fontSize: 'clamp(36px, 6vw, 64px)',
                                    fontWeight: 200,
                                    color: ivory,
                                    letterSpacing: '-0.035em',
                                    lineHeight: 1.05,
                                    margin: '14px 0 16px',
                                }}
                            >
                                Get in touch.{' '}
                                <span
                                    style={{
                                        color: muted,
                                        fontStyle: 'italic',
                                        fontFamily: serif,
                                        fontWeight: 300,
                                    }}
                                >
                                    We read everything.
                                </span>
                            </h1>
                            <p
                                style={{
                                    fontSize: 16,
                                    color: bodyText,
                                    lineHeight: 1.6,
                                    margin: 0,
                                }}
                            >
                                Have a question, refund request, or partnership idea? Send a note and we&apos;ll
                                respond within 24 hours on business days.
                            </p>
                        </div>

                        <div
                            className="lp2__contact-grid"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '320px 1fr',
                                gap: 56,
                                alignItems: 'flex-start',
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                <ContactInfoRow label="EMAIL" value="support@doyourstep.com" />
                                <ContactInfoRow label="RESPONSE TIME" value="Within 24 hours on business days" />
                                <ContactInfoRow
                                    label="KNOWLEDGE BASE"
                                    value="Check the FAQ for quick answers"
                                />
                            </div>

                            <div
                                style={{
                                    background: '#0c0b0a',
                                    border: hairline,
                                    borderRadius: 4,
                                    padding: 'clamp(24px, 4vw, 36px)',
                                }}
                            >
                                {submitted ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                        <div
                                            style={{
                                                fontSize: 56,
                                                color: '#84cc16',
                                                fontFamily: serif,
                                                lineHeight: 1,
                                                marginBottom: 16,
                                            }}
                                        >
                                            ✓
                                        </div>
                                        <h2
                                            style={{
                                                fontSize: 22,
                                                fontWeight: 400,
                                                color: ivory,
                                                margin: '0 0 8px',
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            Message received.
                                        </h2>
                                        <p
                                            style={{
                                                fontSize: 14,
                                                color: muted,
                                                margin: 0,
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            We&apos;ll get back to you as soon as possible.
                                        </p>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={handleSubmit}
                                        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
                                    >
                                        <div
                                            className="lp2__contact-row"
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: 20,
                                            }}
                                        >
                                            <label htmlFor="contact-name">
                                                <span style={labelBase}>NAME</span>
                                                <input
                                                    id="contact-name"
                                                    type="text"
                                                    name="name"
                                                    placeholder="Your name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                    style={inputBase}
                                                />
                                            </label>
                                            <label htmlFor="contact-email">
                                                <span style={labelBase}>EMAIL</span>
                                                <input
                                                    id="contact-email"
                                                    type="email"
                                                    name="email"
                                                    placeholder="your@email.com"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                    style={inputBase}
                                                />
                                            </label>
                                        </div>
                                        <label htmlFor="contact-subject">
                                            <span style={labelBase}>SUBJECT</span>
                                            <select
                                                id="contact-subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                required
                                                style={inputBase}
                                            >
                                                <option value="">Select a subject</option>
                                                <option value="general">General Inquiry</option>
                                                <option value="support">Technical Support</option>
                                                <option value="billing">Billing Question</option>
                                                <option value="feedback">Feedback</option>
                                                <option value="partnership">Partnership</option>
                                            </select>
                                        </label>
                                        <label htmlFor="contact-message">
                                            <span style={labelBase}>MESSAGE</span>
                                            <textarea
                                                id="contact-message"
                                                name="message"
                                                rows={6}
                                                placeholder="How can we help you?"
                                                value={formData.message}
                                                onChange={handleChange}
                                                required
                                                style={{ ...inputBase, resize: 'vertical', minHeight: 140 }}
                                            />
                                        </label>
                                        <button
                                            type="submit"
                                            style={{
                                                background: '#ef4444',
                                                color: ivory,
                                                border: 'none',
                                                padding: '14px 30px',
                                                borderRadius: 4,
                                                fontSize: 13,
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontFamily: mono,
                                                letterSpacing: '0.1em',
                                                alignSelf: 'flex-start',
                                                boxShadow: '0 0 24px rgba(239, 68, 68, 0.35)',
                                            }}
                                        >
                                            SEND MESSAGE →
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <EditorialFooter />
        </div>
    );
};

const ContactInfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div style={{ borderTop: hairline, paddingTop: 16 }}>
        <div
            style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                color: subtle,
                fontFamily: mono,
                fontWeight: 500,
                marginBottom: 6,
            }}
        >
            {label}
        </div>
        <div
            style={{
                fontSize: 16,
                color: ivory,
                letterSpacing: '-0.005em',
            }}
        >
            {value}
        </div>
    </div>
);

export default ContactPage;
